import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faArrowUpRightFromSquare,
} from "@fortawesome/pro-solid-svg-icons";
import s from "./detail.module.css";
import Flag from "react-world-flags";

const {
  useGip,
  useCountries,
  useTechnologies,
  useCompanies,
  useStatuses,
  useGTM,
} = require("hooks");
const { useEffect, useState, useContext, useLayoutEffect } = require("react");
const { GipContext } = require("state");

const ProjectDetail = ({ projectId, countryId }) => {
  const { gips } = useGip();
  const {
    activeProject,
    setActiveProject,
    setHoverProject,
    setShowProjectList,
    markerClicked,
    setMarkerClicked,
    isMobile,
  } = useContext(GipContext);
  // @NOTE: the following hooks, states and useEffects are used to retrieve the actual objects from the ids inside the project object (categories etc..)
  // if need be, all of this can be moved within useGip.setActiveProject

  const { countries: _countries } = useCountries();
  const { technologies: _technologies } = useTechnologies();
  const { companies: _companies } = useCompanies();
  const { statuses: _statuses } = useStatuses();
  const [project, setProject] = useState(null);
  const [country, setCountry] = useState([]);
  const [countries, setCountries] = useState([]);
  const [techs, setTechnologies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [GTMContext, setGTMContext] = useState({
    project: null,
    projectId: null,
  });
  const { dataLayer } = useGTM();

  useEffect(() => {
    if (!projectId) {
      return;
    }
    let project = null;
    if (gips?.length) {
      project = gips.find((p) => p.id === projectId);
    }
    //Evaluate if there is a chance we get an invalid projectId and deal with the case
    //Back to list
    if (project === undefined) {
      console.error("Invalid project ID: " + projectId);
      setActiveProject(null);
      setHoverProject(null);
      setShowProjectList(true);
    }

    setProject(project);
  }, [projectId, gips, setActiveProject, setHoverProject, setShowProjectList]);

  useEffect(() => {
    let country = null; // this one goes into the page header
    let countries = []; // these goes into the page content
    if (_countries?.length && project) {
      country = _countries.find((c) => c.id === countryId);
      if (_countries.length && project) {
        countries = project["taxonomy-country"]
          ?.map((t) => _countries.find((tt) => tt.id === t))
          ?.filter((t) => !!t);
      }
    }
    setCountries(countries);
    setCountry(country);
  }, [countryId, _countries, project]);

  useEffect(() => {
    let techs = [];
    if (_technologies?.length && project) {
      techs = project["gpi-tech"]
        ?.map((t) => _technologies.find((tt) => tt.id === t))
        ?.filter((t) => !!t);
    }
    setTechnologies(techs);
  }, [project, _technologies]);

  useEffect(() => {
    let companies = [];
    if (_companies?.length && project) {
      companies = _companies.filter(
        (c) =>
          project["gpi-company"].length && project["gpi-company"].includes(c.id)
      );
    }
    setCompanies(companies);
  }, [_companies, project]);

  useEffect(() => {
    let statuses = [];
    if (_statuses?.length && project) {
      statuses = project["gpi-statuses"]
        ?.map((t) => _statuses.find((tt) => tt.id === t))
        ?.filter((t) => !!t);
    }
    setStatuses(statuses);
  }, [_statuses, project]);

  useLayoutEffect(() => {
    if (!project) {
      return;
    }
    const context = {
      project: project?.title?.rendered,
      projectId: project.id,
    };

    setGTMContext(context);
    dataLayer.push({
      event: "cefic_gip_map.project.view",
      ...context,
    });
  }, [project, dataLayer]);

  const removeLastLink = (content) => {
    let parser = new DOMParser();
    let modifiedContent = parser.parseFromString(content, "text/html");
    let links = modifiedContent.firstChild.getElementsByTagName("a");
    if (links.length) {
      links[links.length - 1].parentNode.removeChild(links[links.length - 1]);
    }
    return modifiedContent.firstChild.getElementsByTagName("body")[0].innerHTML;
  };

  const addStyleToLastLink = (content) => {
    let parser = new DOMParser();
    let modifiedContent = parser.parseFromString(content, "text/html");
    let links = modifiedContent.firstChild.getElementsByTagName("a");
    if (links.length) {
      let lastLink = links[links.length - 1];
      let link = lastLink.href;
      let linkText = lastLink.innerHTML;
      return (
        <a
          className="text-primary text-sm block mt-4"
          target="_blank"
          rel="noreferrer"
          href={link}
        >
          {linkText}
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-2" />
        </a>
      );
    }
    return;
  };

  return (
    <>
      <div className="sm:h-full sm:absolute sm:right-0 sm:top-0 sm:pt-4 sm:pb-4 sm:pr-4 z-1000">
        <div
          className={`bg-white grow p-6 pb-20 absolute sm:static sm:pb-4 rounded-lg shadow-lg transition-all duration-500 bottom-[-100%] ${activeProject ? "!bottom-[-1%] sm:!bottom-inherit" : "sm:hidden"
            } h-full z-700 overflow-x-hidden w-full sm:w-80 lg:w-96 font-lato`}
        >
          <div className="flex justify-between items-center">
            <div className="flex">
              <Flag
                code={country?.acf?.country_code}
                width="20"
                height="15"
                className="mr-2"
              />
              <div className="uppercase text-gray-400 tracking-widest font-bold text-xs">
                {country && <p>{country.name}</p>}
              </div>
            </div>
            <button
              className="text-primary text-2xl"
              onClick={() => {
                setActiveProject(null);
                setHoverProject(null);
                markerClicked && isMobile
                  ? setMarkerClicked(false)
                  : setShowProjectList(true);
                dataLayer.push({
                  event: "cefic_gip_map.project.close.click",
                  ...GTMContext,
                });
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <article>
            <header className="mb-6">
              <h1
                className="mb-4 mt-2 font-noto text-2xl text-gray-800"
                dangerouslySetInnerHTML={{ __html: project?.title?.rendered }}
              />
              {techs?.length && (
                <div className="flex items-center gap-2">
                  {techs?.map((t) => (
                    <div key={t.id} className="max-w-[75px] text-center">
                      {t?.acf?.icon && (
                        <img
                          src={t.acf.icon}
                          className="w-7 h-9 m-auto"
                          alt={t.name}
                        />
                      )}
                      <span
                        className="text-primary text-xs leading-4 block"
                        dangerouslySetInnerHTML={{ __html: t.name }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </header>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4">
              {techs?.length > 0 && (
                <>
                  <span className={s.title}>Type of investment</span>
                  <span
                    className={s.value}
                    dangerouslySetInnerHTML={{
                      __html: techs.map((t) => t.name).join(", "),
                    }}
                  />
                </>
              )}
              {companies?.length > 0 && (
                <>
                  <span className={s.title}>Companies</span>
                  <span
                    className={s.value}
                    dangerouslySetInnerHTML={{
                      __html: companies.map((t) => t.name).join(", "),
                    }}
                  />
                </>
              )}
              {countries?.length > 0 && (
                <>
                  <span className={s.title}>Countries</span>
                  <span
                    className={s.value}
                    dangerouslySetInnerHTML={{
                      __html: countries
                        .map((t) => {
                          let cities = project?.acf.locations
                            ?.filter((location) => location.country === t.id)
                            ?.map((location) => location.city)
                            ?.join(", ");
                          return `${t.name} ${cities.length ? "(" + cities + ")" : ""
                            }`;
                        })
                        .join(", "),
                    }}
                  />
                </>
              )}
              {statuses?.length > 0 && (
                <>
                  <span className={s.title}>Status</span>
                  <span
                    className={s.value}
                    dangerouslySetInnerHTML={{
                      __html: statuses.map((t) => t.name).join(", "),
                    }}
                  />
                </>
              )}
            </div>
            {project?.content?.rendered && (
              <div className="text-sm">
                <span
                  dangerouslySetInnerHTML={{
                    __html: removeLastLink(project.content.rendered),
                  }}
                />
                {addStyleToLastLink(project.content.rendered)}
              </div>
            )}
          </article>
          <button
            className="text-primary text-sm mt-6"
            onClick={() => {
              setActiveProject(null);
              setHoverProject(null);
              setShowProjectList(true);
              dataLayer.push({
                event: "cefic_gip_map.project.back_to_list.click",
                ...GTMContext,
              });
            }}
          >
            Back to list
          </button>
        </div>
      </div>
    </>
  );
};

export default ProjectDetail;
